import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BrowserProvider, Contract, parseEther } from "ethers";
import toast, { Toaster } from "react-hot-toast";

// Safe BigInt helper for browsers (avoids ESLint no-undef on globalThis/BigInt)
const toBigInt = (v) =>
  (typeof window !== "undefined" && typeof window.BigInt === "function")
    ? window.BigInt(String(v))
    : Number(v);

// Helper: returns post.id if available, otherwise post._id.
const getPostId = (post) => post?.id || post?._id;

// ===== Smart contract config =====
const CONTRACT_ADDRESS = "0x1c713297e32c91694135d552422d6c9f914bdae6";
const CONTRACT_ABI = [
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "nftId", type: "uint256" },
      { indexed: true, internalType: "address", name: "minter", type: "address" },
      { indexed: false, internalType: "uint256", name: "amountPaid", type: "uint256" },
    ],
    name: "MintedToCastle",
    type: "event",
  },
  {
    inputs: [
      { internalType: "uint256", name: "nftId", type: "uint256" },
      { internalType: "uint256", name: "price", type: "uint256" },
    ],
    name: "mintCastle",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "mintedCount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
];

// ===== Wallet + contract helpers =====
async function connectWallet() {
  if (!window.ethereum) {
    toast.error("MetaMask is not installed.");
    throw new Error("No wallet provider");
  }
  try {
    await window.ethereum.request({ method: "eth_requestAccounts" });
    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const userAddress = await signer.getAddress();
    localStorage.setItem("connectedWallet", userAddress);
    return { provider, signer, userAddress };
  } catch (err) {
    toast.error("Wallet connection rejected.");
    throw err;
  }
}

async function getContract(signer) {
  return new Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
}

export default function HomePage() {
  const navigate = useNavigate();

  // Profile (guest)
  const guestProfile = {
    name: "NEP2YN's",
    tag: "CASTLE",
    banner: "",
    profilePic: "/images/nep2ynlogo.jpg",
    bio: "ВАМП CARDS",
    visibility: "public",
  };
  const [profile] = useState(guestProfile);

  // State
  const [posts, setPosts] = useState([]);
  const [lightedPosts, setLightedPosts] = useState({});
  const [mintedPosts, setMintedPosts] = useState({});
  const [filter, setFilter] = useState("RECENT");
  const [selectedPost, setSelectedPost] = useState(null);

  // Fetch NFTs
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://threeamok.onrender.com/api/nfts");
        if (!res.ok) throw new Error("Failed to fetch NFTs");
        const allNFTs = await res.json();
        setPosts(allNFTs);
      } catch (err) {
        toast.error("Could not load NFTs.");
        console.error(err);
      }
    })();
  }, []);

  // ===== Utils =====
  const filterPosts = () => {
    const arr = [...posts];
    switch (filter) {
      case "BRiGHTEST":
        return arr.sort((a, b) => (b.lights || 0) - (a.lights || 0));
      case "NEED LiGHT":
        return arr.sort((a, b) => (a.lights || 0) - (b.lights || 0));
      default:
        return arr;
    }
  };

  // ===== Actions =====
  const toggleLight = (e, id) => {
    e.stopPropagation();
    if (id == null) return;

    const idStr = id.toString();
    const alreadyLighted = !!lightedPosts[idStr];

    setPosts((prev) =>
      prev.map((p) =>
        getPostId(p)?.toString() === idStr
          ? { ...p, lights: Math.max((p.lights || 0) + (alreadyLighted ? -1 : 1), 0) }
          : p
      )
    );

    setLightedPosts((prev) => {
      const copy = { ...prev };
      if (alreadyLighted) {
        delete copy[idStr];
        toast("Light removed.", { icon: "🔦" });
      } else {
        copy[idStr] = true;
        toast.success("Light added!");
      }
      return copy;
    });
  };

  const addRecord = (e, id) => {
    e.stopPropagation();
    if (id == null) return;

    const text = prompt("Add your RECORD -bamn ::3");
    if (!text) return;

    setPosts((prev) =>
      prev.map((p) => {
        const pid = getPostId(p);
        if (pid?.toString() === id.toString()) {
          return {
            ...p,
            records: (p.records || 0) + 1,
            comments: [...(p.comments || []), { text, tag: profile.tag, lights: 0 }],
          };
        }
        return p;
      })
    );

    toast.success("Record added.");
  };

  const saveToLibrary = (nft) => {
    const addr = localStorage.getItem("connectedWallet");
    if (!addr) return;

    const key = `castle_${addr}`;
    const existing = JSON.parse(localStorage.getItem(key) || "[]");

    if (!existing.find((x) => getPostId(x) === getPostId(nft))) {
      const updated = [...existing, nft];
      localStorage.setItem(key, JSON.stringify(updated));
    }
  };

  const addToCastle = async (e, id) => {
    e.stopPropagation();
    if (id == null) return;

    const idStr = id.toString();
    if (mintedPosts[idStr]) {
      toast("Already minted this one.", { icon: "⛫" });
      return;
    }

    const targetPost = posts.find((p) => getPostId(p)?.toString() === idStr);
    if (!targetPost) {
      toast.error("Post not found.");
      return;
    }

    const originalSupply = Number(targetPost.supply) || 0;
    const minted = Number(targetPost.castleAdds) || 0;
    const remaining = targetPost.remaining !== undefined
      ? Number(targetPost.remaining)
      : originalSupply - minted;

    if (remaining <= 0) {
      toast("Sold out.", { icon: "0️⃣" });
      return;
    }

    let nftId;
    if (typeof targetPost.id === "number") {
      nftId = toBigInt(targetPost.id);
    } else if (typeof targetPost.id === "string" && /^\d+$/.test(targetPost.id)) {
      nftId = toBigInt(targetPost.id);
    } else {
      toast.error("This NFT has no numeric on-chain id.");
      return;
    }

    let priceWei;
    try {
      const rawPrice = targetPost.price;

      if (typeof rawPrice === "number") {
        priceWei = parseEther(rawPrice.toString());
      } else if (typeof rawPrice === "string") {
        if (rawPrice.includes(".")) {
          priceWei = parseEther(rawPrice);
        } else if (/^\d+$/.test(rawPrice)) {
          priceWei = toBigInt(rawPrice);
        } else {
          throw new Error("Unrecognized price string");
        }
      } else {
        throw new Error("Price is not number/string");
      }
    } catch (err) {
      console.error("Price parsing failed:", err);
      toast.error("Invalid price format.");
      return;
    }

    const ok = window.confirm(
      `Price: ${targetPost.price} ETH\nOriginal Supply: ${originalSupply}\nRemaining: ${remaining}\n\nConfirm mint in your wallet?`
    );
    if (!ok) return;

    try {
      const { signer } = await connectWallet();
      const contract = await getContract(signer);

      const tx = await contract.mintCastle(nftId, priceWei, { value: priceWei });

      await toast.promise(tx.wait(), {
        loading: "Minting... confirm in wallet",
        success: "Mint confirmed! Added to your castle.",
        error: "Mint failed.",
      });

      saveToLibrary(targetPost);

      setPosts((prev) =>
        prev.map((p) =>
          (getPostId(p) ?? "").toString() === idStr
            ? {
                ...p,
                castleAdds: (p.castleAdds || 0) + 1,
                inCrypt: true,
                remaining:
                  p.remaining !== undefined
                    ? Math.max(Number(p.remaining) - 1, 0)
                    : Math.max((Number(p.supply) || 0) - ((Number(p.castleAdds) || 0) + 1), 0),
              }
            : p
        )
      );

      setMintedPosts((prev) => ({ ...prev, [idStr]: true }));

      const addr = localStorage.getItem("connectedWallet") || "";
      await fetch(
        `https://threeamok.onrender.com/api/nfts/${targetPost.id}/mint?owner=${encodeURIComponent(addr)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ inCrypt: true }),
        }
      );
    } catch (error) {
      console.error("Minting failed:", error);
      toast.error("Minting failed. Please try again. -bamn ::E");
    }
  };

  const deletePost = async (e, id, mongoId) => {
    e.stopPropagation();
    const targetId = mongoId || id;

    if (!targetId) {
      toast.error("Missing post id.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this post?")) return;

    const addr = localStorage.getItem("connectedWallet") || "";
    try {
      await toast.promise(
        (async () => {
          const res = await fetch(
            `https://threeamok.onrender.com/api/nfts/${targetId}?owner=${encodeURIComponent(addr)}`,
            {
              method: "DELETE",
              headers: { "Content-Type": "application/json" },
            }
          );
          if (!res.ok) throw new Error("Server refused delete");
        })(),
        {
          loading: "Deleting...",
          success: "Post deleted.",
          error: "Delete failed.",
        }
      );

      setPosts((prev) =>
        prev.filter((p) => (p._id || p.id)?.toString() !== targetId.toString())
      );

      setLightedPosts((prev) => {
        const copy = { ...prev };
        delete copy[targetId.toString()];
        return copy;
      });

      setMintedPosts((prev) => {
        const copy = { ...prev };
        delete copy[targetId.toString()];
        return copy;
      });
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const toggleVisibility = (e, id) => {
    e.stopPropagation();
    if (id == null) return;

    setPosts((prev) =>
      prev.map((p) => {
        const pid = getPostId(p);
        if (pid?.toString() === id.toString()) {
          const newVis = p.visibility === "private" ? "public" : "private";
          toast.success(`Visibility: ${newVis.toUpperCase()}`);
          return { ...p, visibility: newVis };
        }
        return p;
      })
    );
  };

  const closeModal = () => setSelectedPost(null);

  return (
    <div className="min-h-screen bg-brandBlack text-brandWhite p-6">
      <Toaster position="top-center" />

      <div className="text-center py-1 px-1">
        <h1 className="text-5xl font-gothic text-gray-500">3AMOK</h1>
        <h1 className="text-5xl font-gothic text-gray-400">✠ ☉ ☽ ⛤</h1>
        <h1 className="text-5xl font-gothic text-white mt-12">NEP2YN ВАМПМИР</h1>
        <h1 className="text-5xl font-gothic text-white mt-4">☉</h1>
        <p className="text-lg mt-4 italic text-gray-400">
          SOUNDS , iMAGES , AND WORDS OF THiS ВАМПМИР .
        </p>
        <p className="max-w-xl mt-6 mx-auto text-sm text-gray-500"></p>
        <button
          onClick={() => navigate("/nep2yn")}
          className="mt-4 px-4 py-2 border border-white text-white hover:bg-white hover:text-black transition"
        >
          more about NEP2YN
        </button>
      </div>

      <div className="w-full h-48 relative overflow-hidden mb-">
        <h1 className="text-5xl font-gothic text-white text-center">
          {profile.banner}
        </h1>
      </div>

      <div className="flex flex-col items-center mb-6">
        <img
          src={profile.profilePic}
          alt="Profile"
          className="w-24 h-24 rounded-full border-4 border-gray-400 mb-2"
        />
        <h2 className="text-3xl font-gothic mb-1">{profile.name}</h2>
        <p className="text-lg font-gothic text-gray-400 mb-1">{profile.tag}</p>
        <p className="text-lg font-gothic text-gray-300 text-center max-w-xl">
          {profile.bio}
        </p>
      </div>

      <div className="flex justify-around text-lg font-gothic mb-6">
        <a href={`/gallery/${profile.tag}`} className="hover:text-crypticGlow">
          ◯ GALLERY
        </a>
        <a href={`/records/${profile.tag}`} className="hover:text-crypticGlow">
          ⊖ RECORDS
        </a>
        <a href={`/sounds/${profile.tag}`} className="hover:text-crypticGlow">
          ⨂ SOUNDS
        </a>
        <button
          type="button"
          onClick={() => navigate("/crypt")}
          className="hover:text-crypticGlow cursor-pointer bg-transparent border-none p-0"
        >
          ✠ CRYPT
        </button>
      </div>

      <div className="text-center mb-4">
        <button
          onClick={() => setFilter("RECENT")}
          className={`p-2 mx-2 ${filter === "RECENT" ? "bg-gray-700 text-white" : "bg-gray-400"}`}
        >
          MOST RECENT
        </button>
        <button
          onClick={() => setFilter("BRiGHTEST")}
          className={`p-2 mx-2 ${filter === "BRiGHTEST" ? "bg-gray-700 text-white" : "bg-gray-400"}`}
        >
          BRiGHTEST
        </button>
        <button
          onClick={() => setFilter("NEED LiGHT")}
          className={`p-2 mx-2 ${filter === "NEED LiGHT" ? "bg-gray-700 text-white" : "bg-gray-400"}`}
        >
          NEED LiGHT
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filterPosts().map((post) => {
          const postId = getPostId(post);
          const originalSupply = Number(post.supply) || 0;
          const minted = Number(post.castleAdds) || 0;
          const remaining =
            post.remaining !== undefined
              ? Number(post.remaining)
              : Math.max(originalSupply - minted, 0);

          return (
            <div
              key={postId}
              onClick={() => setSelectedPost(post)}
              className="border border-gray-700 rounded overflow-hidden p-4 cursor-pointer flex flex-col justify-between"
            >
              <div className="h-40">
                {post.type === "pictureVideo" && (
                  <>
                    {post.video ? (
                      <video
                        controls
                        className="w-full h-full object-cover"
                        poster={post.image}
                      >
                        <source src={post.video} type="video/mp4" />
                      </video>
                    ) : (
                      <img
                        src={post.image}
                        alt={post.title || "Picture"}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </>
                )}

                {post.type === "writtenRecord" && (
                  <div className="h-full flex items-center justify-center bg-gray-800 text-gray-300 font-gothic text-center p-2">
                    {post.title ||
                      (post.description && post.description.slice(0, 50)) ||
                      "No Title"}
                  </div>
                )}

                {post.type === "audio" && (
                  <div className="h-full flex flex-col">
                    <img
                      src={post.image}
                      alt={post.title || "Audio Thumbnail"}
                      className="w-full h-2/3 object-cover"
                    />
                    <div className="mt-1">
                      <audio controls className="w-full">
                        <source src={post.audio} type="audio/mpeg" />
                      </audio>
                    </div>
                  </div>
                )}
              </div>

              {post.type === "pictureVideo" && post.title && (
                <h2 className="text-lg font-gothic mt-2 text-center">{post.title}</h2>
              )}

              <p className="text-center text-sm mt-1">
                Price: {post.price ?? "N/A"} ETH | Supply: {post.supply ?? "N/A"} | Remaining: {remaining}
              </p>

              {remaining <= 0 ? (
                <p className="text-center text-red-500 font-bold mt-1">SOLD OUT</p>
              ) : null}

              <div className="flex justify-around mt-4">
                <button onClick={(e) => toggleLight(e, postId)}>
                  ☉ LiGHT {post.lights || 0}
                </button>
                <button onClick={(e) => addRecord(e, postId)}>
                  + RECORD {post.records || 0}
                </button>
                <button onClick={(e) => addToCastle(e, postId)}>
                  ⛫ MINT ({remaining} left)
                </button>
              </div>

              <div className="flex justify-around mt-2">
                <button
                  onClick={(e) => deletePost(e, post.id, post._id)}
                  className="text-red-400 underline"
                >
                  Delete
                </button>
                <button
                  onClick={(e) => toggleVisibility(e, postId)}
                  className="text-blue-400 underline"
                >
                  {post.visibility === "private" ? "PUBLIC" : "MY EYES ONLY"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center">
          <div className="bg-brandBlack p-6 rounded-lg w-full max-w-md">
            {selectedPost.type === "pictureVideo" && (
              <>
                {selectedPost.video ? (
                  <video
                    controls
                    className="w-full mb-4 object-cover"
                    poster={selectedPost.image}
                  >
                    <source src={selectedPost.video} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={selectedPost.image}
                    alt={selectedPost.title || "Picture"}
                    className="w-full mb-4 object-cover"
                  />
                )}
              </>
            )}

            {selectedPost.type === "audio" && (
              <>
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title || "Audio Thumbnail"}
                  className="w-full mb-2 object-cover"
                />
                <audio controls className="w-full mb-4">
                  <source src={selectedPost.audio} type="audio/mpeg" />
                </audio>
              </>
            )}

            {selectedPost.type === "writtenRecord" && (
              <div className="h-40 flex items-center justify-center bg-gray-800 text-gray-300 font-gothic text-center p-2 mb-4">
                {selectedPost.title ||
                  (selectedPost.description && selectedPost.description) ||
                  "No Title"}
              </div>
            )}

            <h2 className="text-xl font-gothic mb-2">{selectedPost.title}</h2>
            <p className="text-gray-300 mb-4">{selectedPost.description}</p>

            {(() => {
              const originalSupply = Number(selectedPost.supply) || 0;
              const minted = Number(selectedPost.castleAdds) || 0;
              const remaining =
                selectedPost.remaining !== undefined
                  ? Number(selectedPost.remaining)
                  : Math.max(originalSupply - minted, 0);

              return (
                <>
                  <p className="text-center text-sm mb-4">
                    Price: {selectedPost.price ?? "N/A"} ETH | Supply: {selectedPost.supply ?? "N/A"} | Remaining: {remaining}
                  </p>

                  {remaining <= 0 ? (
                    <p className="text-center text-red-500 font-bold mb-4">SOLD OUT</p>
                  ) : null}

                  <div className="flex justify-around mb-4">
                    <button onClick={(e) => toggleLight(e, getPostId(selectedPost))}>
                      ☉ {selectedPost.lights || 0}
                    </button>
                    <button onClick={(e) => addRecord(e, getPostId(selectedPost))}>
                      + {selectedPost.records || 0}
                    </button>
                    <button onClick={(e) => addToCastle(e, getPostId(selectedPost))}>
                      ⛫ MINT ({remaining} left)
                    </button>
                  </div>
                </>
              );
            })()}

            <div className="flex justify-around mb-4">
              <button
                onClick={(e) => deletePost(e, selectedPost.id, selectedPost._id)}
                className="text-red-400 underline"
              >
                Delete
              </button>
              <button
                onClick={(e) => toggleVisibility(e, getPostId(selectedPost))}
                className="text-blue-400 underline"
              >
                {selectedPost.visibility === "private" ? "PUBLIC" : "MY EYES ONLY"}
              </button>
            </div>

            {selectedPost.comments && selectedPost.comments.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-gothic mb-2">Records</h3>
                <ul className="list-none">
                  {selectedPost.comments.map((comment, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <button
                        className="text-crypticGlow underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/castle/${comment.tag}`);
                        }}
                      >
                        {comment.tag}
                      </button>
                      <span className="text-gray-300">{comment.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={closeModal}
              className="block mx-auto bg-gray-700 hover:bg-gray-500 text-white px-4 py-2 rounded mt-4"
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}